import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { getContact, getOrganizations } from '../../../lib/supabase/contacts';
import { supabase } from '../../../lib/supabase/client';
import { getOrganizationProfile } from '../../../lib/supabase/organization';
import { useAuthStore } from '../../../store/authStore';
import { useEntity } from '../../../hooks/useEntity';
import type { Contact } from '../../../types/contacts';

interface EditContactFormProps {
  contactId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditContactForm({ contactId, onClose, onSuccess }: EditContactFormProps) {
  const { user } = useAuthStore();
  const { entity } = useEntity();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    organizationId: '',
    type: 'customer' as 'customer' | 'team' | 'supplier' | 'partner',
    title: '',
    notes: '',
    source: 'gekko'
  });

  const [organizations, setOrganizations] = useState<Array<{ id: string, name: string }>>([]);
  const [emails, setEmails] = useState<Array<{ id?: string, email: string, type: string, is_primary?: boolean }>>([]);
  const [phones, setPhones] = useState<Array<{ id?: string, phone: string, type: string, is_primary?: boolean }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyOrganization, setCompanyOrganization] = useState<{id: string, name: string} | null>(null);
  const [showNewOrgInput, setShowNewOrgInput] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [creatingOrg, setCreatingOrg] = useState(false);

  useEffect(() => {
    if (entity?.id) {
      loadData();
    }
  }, [contactId, entity?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load all data in parallel
      const [contact, orgs, profile] = await Promise.all([
        getContact(contactId),
        getOrganizations(entity!.id),
        getOrganizationProfile()
      ]);
      
      if (!contact) {
        throw new Error('Contact not found');
      }

      // Set form data
      setFormData({
        firstName: contact.first_name,
        lastName: contact.last_name,
        organizationId: contact.organization_id || '',
        type: contact.type as 'customer' | 'team' | 'supplier' | 'partner',
        title: contact.title || '',
        notes: contact.notes || '',
        source: contact.source || 'gekko'
      });

      // Set emails and phones
      setEmails(contact.emails?.map(email => ({
        id: email.id,
        email: email.email,
        type: email.type,
        is_primary: email.is_primary
      })) || []);

      setPhones(contact.phones?.map(phone => ({
        id: phone.id,
        phone: phone.phone,
        type: phone.type,
        is_primary: phone.is_primary
      })) || []);

      // Set organizations
      setOrganizations(orgs || []);

      // Set company organization
      if (profile?.organization?.id) {
        setCompanyOrganization({
          id: profile.organization.id,
          name: profile.organization.name
        });
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load contact details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const createNewOrganization = async (name: string, entityId: string): Promise<string | null> => {
    if (!name.trim()) {
      throw new Error('Organization name is required');
    }

    try {
      setCreatingOrg(true);
      const orgData = {
        name: name.trim(),
        entity_id: entityId,
        type: formData.type === 'customer' ? 'client' : 
              formData.type === 'supplier' ? 'supplier' : 
              formData.type === 'partner' ? 'partner' : 'client'
      } as const;
      
      console.log('Creating organization with data:', orgData);
      
      // Direct Supabase call to create organization
      const { data: orgResult, error: orgError } = await supabase
        .from('organizations')
        .insert([orgData])
        .select()
        .single();
      
      if (orgError) {
        throw new Error(`Failed to create organization: ${orgError.message}`);
      }
      
      if (!orgResult) {
        throw new Error('Failed to create organization: No data returned');
      }
      
      // Immediately reload organizations to ensure the new one is available
      await loadData();
      
      return orgResult.id;
    } catch (error) {
      console.error('Failed to create organization:', error);
      throw new Error('Failed to create organization. Please try again.');
    } finally {
      setCreatingOrg(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    if (!entity?.id) {
      setError('No entity found. Please refresh and try again.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      console.log('Starting contact update...');
      
      // Determine organization ID
      let finalOrgId = null;
      
      if (showNewOrgInput && newOrgName.trim()) {
        // Create new organization
        finalOrgId = await createNewOrganization(newOrgName, entity.id);
        if (!finalOrgId) {
          throw new Error('Failed to create organization');
        }
      } else if (formData.type === 'team' && companyOrganization) {
        finalOrgId = companyOrganization.id;
      } else if (formData.organizationId) {
        finalOrgId = formData.organizationId;
      }

      // Update contact using direct Supabase call
      const { data: contactData, error: contactError } = await supabase
        .from('contacts')
        .update({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          type: formData.type,
          title: formData.title.trim() || null,
          organization_id: finalOrgId,
          notes: formData.notes.trim() || null,
          organization_relationship: formData.type === 'team' ? 'internal' : 'external',
          source: formData.source
        })
        .eq('id', contactId)
        .select()
        .single();

      if (contactError) {
        throw new Error(`Failed to update contact: ${contactError.message}`);
      }

      console.log('Contact updated successfully');

      // Handle emails - delete all and recreate (simpler approach)
      await supabase.from('contact_emails').delete().eq('contact_id', contactId);
      
      if (emails.length > 0) {
        const emailRecords = emails
          .filter(e => e.email.trim())
          .map((email, index) => ({
            contact_id: contactId,
            email: email.email.trim(),
            type: email.type,
            is_primary: index === 0
          }));

        if (emailRecords.length > 0) {
          const { error: emailError } = await supabase
            .from('contact_emails')
            .insert(emailRecords);
          
          if (emailError) {
            throw new Error(`Failed to update emails: ${emailError.message}`);
          }
        }
      }

      // Handle phones - delete all and recreate (simpler approach)
      await supabase.from('contact_phones').delete().eq('contact_id', contactId);
      
      if (phones.length > 0) {
        const phoneRecords = phones
          .filter(p => p.phone.trim())
          .map((phone, index) => ({
            contact_id: contactId,
            phone: phone.phone.trim(),
            type: phone.type,
            is_primary: index === 0
          }));

        if (phoneRecords.length > 0) {
          const { error: phoneError } = await supabase
            .from('contact_phones')
            .insert(phoneRecords);
          
          if (phoneError) {
            throw new Error(`Failed to update phones: ${phoneError.message}`);
          }
        }
      }

      console.log('Contact update completed successfully');
      onSuccess();
    } catch (err: any) {
      console.error('Error updating contact:', err);
      setError(err.message || 'Failed to update contact. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Edit Contact</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600">Loading contact details...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Edit Contact</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                required
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
                required
                disabled={saving}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              disabled={saving}
            >
              <option value="customer">Customer</option>
              <option value="team">Team</option>
              <option value="supplier">Supplier</option>
              <option value="partner">Partner</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Organization</label>
            <div className="mt-1 space-y-2">
              {!showNewOrgInput ? (
                <>
                  <select
                    value={formData.organizationId || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, organizationId: e.target.value }))}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2"
                    disabled={saving || formData.type === 'team'}
                  >
                    <option value="">Select Organization</option>
                    {formData.type === 'team' && companyOrganization ? (
                      <option value={companyOrganization.id}>{companyOrganization.name} (Your Company)</option>
                    ) : (
                      organizations.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))
                    )}
                  </select>
                  {formData.type !== 'team' && (
                    <button
                      type="button"
                      onClick={() => setShowNewOrgInput(true)}
                      className="text-sm text-blue-500 hover:text-blue-600"
                      disabled={saving}
                    >
                      + Add New Organization
                    </button>
                  )}
                </>
              ) : (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    className="block w-full rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="Enter organization name"
                    disabled={saving || creatingOrg}
                    required
                  />
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewOrgInput(false);
                        setNewOrgName('');
                      }}
                      className="text-sm text-gray-500 hover:text-gray-600"
                      disabled={saving || creatingOrg}
                    >
                      Cancel
                    </button>
                    {creatingOrg && (
                      <span className="text-sm text-blue-500">Creating organization...</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              disabled={saving}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Email Addresses</label>
              <button
                type="button"
                onClick={() => setEmails(prev => [...prev, { email: '', type: 'work' }])}
                className="text-sm text-blue-500 hover:text-blue-600"
                disabled={saving}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {emails.map((email, index) => (
              <div key={index} className="flex space-x-2 mb-2">
                <input
                  type="email"
                  value={email.email}
                  onChange={(e) => {
                    const newEmails = [...emails];
                    newEmails[index].email = e.target.value;
                    setEmails(newEmails);
                  }}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="Email address"
                  disabled={saving}
                />
                <select
                  value={email.type}
                  onChange={(e) => {
                    const newEmails = [...emails];
                    newEmails[index].type = e.target.value;
                    setEmails(newEmails);
                  }}
                  className="w-32 rounded-lg border border-gray-300 px-3 py-2"
                  disabled={saving}
                >
                  <option value="work">Work</option>
                  <option value="personal">Personal</option>
                  <option value="other">Other</option>
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const newEmails = [...emails];
                    newEmails.splice(index, 1);
                    setEmails(newEmails);
                  }}
                  className="text-red-500 hover:text-red-700"
                  disabled={saving}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
            {emails.length === 0 && (
              <button
                type="button"
                onClick={() => setEmails([{ email: '', type: 'work' }])}
                className="text-sm text-blue-500 hover:text-blue-600"
                disabled={saving}
              >
                + Add Email
              </button>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Phone Numbers</label>
              <button
                type="button"
                onClick={() => setPhones(prev => [...prev, { phone: '', type: 'work' }])}
                className="text-sm text-blue-500 hover:text-blue-600"
                disabled={saving}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {phones.map((phone, index) => (
              <div key={index} className="flex space-x-2 mb-2">
                <input
                  type="tel"
                  value={phone.phone}
                  onChange={(e) => {
                    const newPhones = [...phones];
                    newPhones[index].phone = e.target.value;
                    setPhones(newPhones);
                  }}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2"
                  placeholder="Phone number"
                  disabled={saving}
                />
                <select
                  value={phone.type}
                  onChange={(e) => {
                    const newPhones = [...phones];
                    newPhones[index].type = e.target.value;
                    setPhones(newPhones);
                  }}
                  className="w-32 rounded-lg border border-gray-300 px-3 py-2"
                  disabled={saving}
                >
                  <option value="work">Work</option>
                  <option value="mobile">Mobile</option>
                  <option value="home">Home</option>
                  <option value="other">Other</option>
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const newPhones = [...phones];
                    newPhones.splice(index, 1);
                    setPhones(newPhones);
                  }}
                  className="text-red-500 hover:text-red-700"
                  disabled={saving}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
            {phones.length === 0 && (
              <button
                type="button"
                onClick={() => setPhones([{ phone: '', type: 'work' }])}
                className="text-sm text-blue-500 hover:text-blue-600"
                disabled={saving}
              >
                + Add Phone
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              disabled={saving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Source</label>
            <select
              value={formData.source}
              onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              disabled={saving}
            >
              <option value="gekko">Gekko (Manual Entry)</option>
              <option value="import">Import</option>
              <option value="csv">CSV Upload</option>
              <option value="google">Google Contacts</option>
              <option value="outlook">Outlook</option>
              <option value="salesforce">Salesforce</option>
              <option value="hubspot">HubSpot</option>
              <option value="api">API</option>
              <option value="manual">Manual Entry</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || creatingOrg}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-2"
            >
              {saving || creatingOrg ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{creatingOrg ? 'Creating Organization...' : 'Saving...'}</span>
                </>
              ) : (
                <span>Save Changes</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}